<script lang="ts">
	import type { PoiCategoryTrackerName } from '$lib/services/tracking/googleAnalytics/events';
	import type { PoisStoreContent } from './(map)/types';
	import { browser, dev } from '$app/environment';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { page as pageState } from '$app/state';
	import { page, updated } from '$app/stores';
	import bibListen from '$assets/toast/bib-listen.webp';
	import { env } from '$env/dynamic/public';
	import MarketingModal from '$lib/components/advertising/marketing/MarketingModal.svelte';
	import Toast from '$lib/components/atoms/toast/Toast.svelte';
	import NotificationContainer from '$lib/components/notifications/NotificationContainer.svelte';
	import { isAccommodationMichelinPoi, isRestaurantMichelinPoi } from '$lib/components/pois/utils';
	import SEOHead from '$lib/components/seo/SEOHead.svelte';
	import { marketingEvent } from '$lib/constants/marketingModal';
	import { bvaSurvey } from '$lib/constants/toastBvaSurvey';
	import { onAdServingMessage } from '$lib/services/advertising/orange/adserving';
	import { trackPageView } from '$lib/services/tracking/googleAnalytics/tracking';
	import { useMetaPixel } from '$lib/services/tracking/metaPixel/metaPixel';
	import { metaTrackDatasheetViewContent, metaTrackPageView } from '$lib/services/tracking/metaPixel/tracking';
	import { mouseflowIntegration, useMouseflow } from '$lib/services/tracking/mouseflow/mouseflow';
	import { useTeadsPixel } from '$lib/services/tracking/teadsPixel/teadsPixel';
	import {
		isDatasheetPage,
		isMapPoiListPage,
	} from '$lib/services/urls/routeIds';
	import { getSearchParamsStore, parseSearchParams } from '$lib/services/urls/searchParams';
	import { setLanguageTag } from '$paraglide/runtime';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import '@unocss/reset/sanitize/sanitize.css';
	import '@unocss/reset/sanitize/assets.css';
	import '../styles/global.css';
	import 'virtual:uno.css';

	$: ({ seoDataStore, poisStore } = $page.data);
	// set language tag in +layout.svelte to ensure it is consistent during rendering
    // this relies on svelte render being sync!
	setLanguageTag($page.data.lang);

	// ensure we init search params store in root layout context so it is shared with all components
	getSearchParamsStore(true, $page.data.locale);

	onMount(() => {
		// cleanup old service worker from legacy app
		navigator.serviceWorker?.getRegistrations().then((registrations) => {
			for (const registration of registrations) {
				try {
					// ensure we only unregister the legacy worker, not others from web extensions
                    // or possible future workers of our own app
					if (
						registration.active?.scriptURL.startsWith(
							`${location.origin}/serviceWorker.js?version=static/`,
						)
					) {
						registration.unregister();
					}
				} catch {
				// ignore
				}
			}
		});

		// mark initial state to that our goBack logic can detect it and use fallback url instead
        // see goBackWithFallback in searchParams.ts
		history.replaceState({ ...history.state, goback_use_fallback: true }, '');
		// mark hydration
		document?.body?.setAttribute('data-testid', 'hydrated');

		window.addEventListener('message', onMessageReceive);
		return () => {
			window.removeEventListener('message', onMessageReceive);
		};
	});

	function onMessageReceive(event: MessageEvent) {
		if (event.data.type === 'didomi_clickagree') {
			// Listen for Didomi notice agree
			trackPageViewOnNavigate();
		} else if (event.data.type === 'viamichelin_ad_serving') {
			// Listen for ad messages for adserving
			onAdServingMessage(event);
		}
	}

	// trigger full page reload if there was an update to the application
    // see https://kit.svelte.dev/docs/configuration#version
	beforeNavigate(({ willUnload, to }) => {
		if ($updated && !willUnload && to?.url) {
			location.href = to.url.href;
		}
	});

	function getPoisStoreContent(): PoisStoreContent | undefined {
		return poisStore && get(poisStore);
	}

	function trackPageViewOnNavigate() {
		trackPageView(pageState);
		metaTrackPageView();

		if ($page.data?.pagename) {
			mouseflowIntegration.newPageView($page.data.pagename);
		}
	}

	function trackMetaPixelDatasheetViewContentOnNavigate(route: { id: string | null } | undefined) {
		if (!route?.id || !isDatasheetPage(route)) {
			return;
		}

		const poisStoreContent = getPoisStoreContent();
		const poi = poisStoreContent?.selectedPoiData;
		const isMichelinSelection = isAccommodationMichelinPoi(poi) || isRestaurantMichelinPoi(poi) || false;

		metaTrackDatasheetViewContent({
			content_category: $page.data.pagename?.split('.')[0] as PoiCategoryTrackerName,
			michelin_selection: isMichelinSelection ? 'true' : 'false',
		});
	}

	afterNavigate(({ to, from, type }) => {
		try {
			if (!from) {
				trackMetaPixelDatasheetViewContentOnNavigate(to?.route);
				// track page view on initial load
				trackPageViewOnNavigate();
				return;
			}

			if (type === 'goto' && from && (!from.url || from.route.id == null)) {
				// workaround to avoid error when entering mobile/roadsheet route
                // which has an unidentified goto call
				return;
			}

			if (from && to) {
				if (to.url?.pathname !== from.url?.pathname) {
					trackMetaPixelDatasheetViewContentOnNavigate(to.route);

					// track page view on route change
					trackPageViewOnNavigate();
					return;
				}

				if (isMapPoiListPage(from.route) && isMapPoiListPage(to.route)) {
					const fromSelectedAddress = from.url.searchParams.get('selectedAddress');
					const toSelectedAddress = to.url.searchParams.get('selectedAddress');

					// ensure we send page view event only when there is a search param 'selectedAddress.address' value
					const toSelectedAddressValue = parseSearchParams(to.url.searchParams).selectedAddress?.address;

					if (fromSelectedAddress !== toSelectedAddress && toSelectedAddressValue) {
						// track page view on selectedAddress search param change from map poi list page
						trackPageViewOnNavigate();
					}
				}
			}
		} catch (e) {
			// prevent errors in tracking code from reaching the application
			if (dev) {
				console.error('error occured in afterNavigate tracking code', e);
			}
		}
	});

	useMouseflow();
	useMetaPixel();
	useTeadsPixel();
	$: isErrorPage = $page.status >= 400;
	const surveyOptions = $page.data.lang === 'fr' && env.PUBLIC_FEATURE_BVA_TOAST_ENABLED === 'true' && bvaSurvey.active ? bvaSurvey[$page.data.lang] : undefined;
</script>

{#if !isErrorPage}
	<SEOHead seo={$seoDataStore} lang={$page.data.lang} url={$page.url} />
{/if}

<slot />
{#if browser}
	<NotificationContainer />

	{#if marketingEvent.enabled && marketingEvent.matchGuard($page.data.locale)}
		<MarketingModal />
	{/if}

	{#if surveyOptions}
		<Toast
			description={surveyOptions.description}
			ctaText={surveyOptions.cta}
			ctaLink={surveyOptions.href}
			delay={bvaSurvey.delay}
			maxNbOpenPerSession={bvaSurvey.maxNbOpenPerSession}
			sessionKey={bvaSurvey.sessionKey}
			bgImage={bibListen}
		/>
	{/if}
{/if}
